import { useMemo } from 'react';
import { onlyDateToServer, useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { Pagination, PaginationParams } from '@/common/types';
import { API_URL, ILetterOfMedicalNecessityDto } from '@/dtos';
import { LettersOfMedicalNecessity, LMNClaim } from '@/modules/MyDocuments/myDocuments.types';

export enum QueryKey {
  LETTERS_OF_MEDICAL_NECESSITY_BY_CLAIM = 'LETTERS_OF_MEDICAL_NECESSITY_BY_CLAIM',
}

interface LMNClaimDto {
  claim_id: number;
  provider_name: string;
  service_id: number;
  requested_amount: number;
  service_on: string;
}
export interface LettersOfMedicalNecessityPayload {
  id?: number;
  name?: string;
  description?: string;
  valid_from?: string;
  valid_to?: string;
  document_id?: number;
  employee_id?: number;
  organization_id?: number;
  organization_path?: string;
  claim_ids?: number[];
  claims?: LMNClaimDto[];
  provider_id?: number;
  created_at?: string;
  file_url?: string;
}

interface Props extends Partial<PaginationParams> {
  queryKey?: QueryKey;
  employeeId?: string;
  validOn?: string;
  disabled?: boolean;
}

const useGetLettersOfMedicalNecessityQuery = ({
  queryKey,
  page,
  perPage,
  searchString,
  employeeId,
  validOn,
  disabled,
}: Props) => {
  const { data, isLoading } = useQuery(
    [queryKey || API_URL.LETTERS_OF_MEDICAL_NECESSITY, page, perPage, searchString, employeeId],
    () =>
      // TODO: fix types Problem with Pagination for ILetterOfMedicalNecessityDto
      api.get<Pagination<ILetterOfMedicalNecessityDto>>(API_URL.LETTERS_OF_MEDICAL_NECESSITY, {
        params: {
          ...(searchString ? { search_value: searchString } : {}),
          page: page && page >= 0 ? page - 1 : 0,
          size: perPage || 10,
          employee_id: employeeId,
          ...(validOn ? { valid_on: onlyDateToServer(validOn) } : {}),
        },
      }),
    {
      enabled: !disabled,
    }
  );

  const lettersOfMedicalNecessity: LettersOfMedicalNecessity[] = useMemo(
    () =>
      data?.data?.content?.map((lettersOfMedicalNecessity) => ({
        id: toString(lettersOfMedicalNecessity?.id),
        name: lettersOfMedicalNecessity?.name,
        description: lettersOfMedicalNecessity?.description,
        validFrom: (lettersOfMedicalNecessity?.valid_from || '') as string,
        validTo: (lettersOfMedicalNecessity?.valid_to || '') as string,
        documentId: toString(lettersOfMedicalNecessity?.document_id),
        employeeId: toString(lettersOfMedicalNecessity?.employee_id),
        organizationId: toString(lettersOfMedicalNecessity?.organization_id),
        organizationPath: lettersOfMedicalNecessity?.organization_path,
        claimIds: lettersOfMedicalNecessity?.claims?.map((item) => `${item || ''}`) || [],
        providerId: '',
        createdAt: (lettersOfMedicalNecessity?.created_at || '') as string,
        // TODO: this field is missing
        // @ts-ignore
        thumbnailUrl: lettersOfMedicalNecessity?.thumbnail_url,
        claims: lettersOfMedicalNecessity?.claims?.map((claim) => {
          const currentItem: LMNClaim = {
            claimId: `${claim?.claim_id || ''}`,
            providerName: claim?.provider_name,
            serviceId: `${claim?.service_id || ''}`,
            requestedAmount: claim?.requested_amount,
            serviceOn: `${claim?.service_on || ''}` as string,
          };
          return currentItem;
        }),
      })) || [],
    [data]
  );

  return {
    lettersOfMedicalNecessity,
    total: data?.data?.totalElements || 0,
    isLoading,
  };
};

export default useGetLettersOfMedicalNecessityQuery;
